import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  FormHelperText,
  Grid,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { object, reach, string } from 'yup';

import { videoRequestService } from '../../services/videoRequest';

import { classNames } from '../../utils';
import { showToast } from '../../utils/toast';

import SwitchUi from '../../components/UI/Switch/Switch';
import { ButtonUi } from '../../components/UI/Button/Button';
import TableHeader from '../../components/UI/TableHeader/TableHeader';
import { TextFieldUi } from '../../components/UI/TextField/TextField';
import CombineToggle from '../../components/UI/CombineToggle/CombineToggle';
import { requestVideoStyle } from './style';

const initRequestFormData = {
  organization: '',
  therapist: '',
  email: '',
  treatmentType: 'exposure',
  interactiveSubject: false,
  treatmentIndication: '',
  targetGroup: '',
  gender: '',
  ethnicity: '',
  locations: '',
  videoAim: '',
  dialogueEmotion: '',
  furtherNotes: '',
};

const switchOptions = [
  {
    content: 'forms.treatmentType.exposure',
    type: 'text',
    variable: 'exposure',
  },
  {
    content: 'forms.treatmentType.relaxation',
    type: 'text',
    variable: 'relaxation',
  },
];

export const RequestVideo = memo(() => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width: 1025px)');
  const { requestVideo } = requestVideoStyle;

  const { authentication } = useSelector((state) => state);
  const {
    practitioner,
    foundUser: { email },
  } = authentication;
  const [requestData, setRequestData] = useState(initRequestFormData);
  const [validationErrors, setValidationErrors] = useState({});
  const [requestIsloading, setRequestIsloading] = useState(false);

  const isSubmitionDisabled =
    Object.keys(validationErrors).length > 0 || requestIsloading;

  const isExposure = useMemo(
    () => requestData?.treatmentType === 'exposure',
    [requestData?.treatmentType],
  );

  const multiLineInputs = [
    'videoAim',
    'locations',
    'dialogueEmotion',
    'furtherNotes',
  ];

  const readOnlyInputs = ['organization', 'therapist'];
  const hiddenInputs = ['email'];
  const headerInputs = [{ key: 'gender', title: 'forms.treatment.subject' }];

  const controlsValidators = useMemo(() => {
    const {
      dialogueEmotion,
      furtherNotes,
      treatmentType,
      interactiveSubject,
      ...requiredFields
    } = requestData;
    const validators = {};
    const requiredFieldsKey = Object.keys(requiredFields);
    Object.keys(requestData).forEach((control) => {
      if (!requiredFieldsKey.includes(control)) {
        validators[control] = string().notRequired();
        return;
      }
      validators[control] = string().required(t('errors.validation.required'));
    });
    return validators;
  }, [requestData, t]);

  /**
   * Get Validation schema for the form fields
   */
  const validationSchema = useMemo(
    () =>
      object().shape({
        ...controlsValidators,
      }),
    [controlsValidators],
  );

  /**
   * Validates form fields and sets error
   * @control field control name
   * @value field value
   */
  const validateControl = useCallback(
    async (control, value) => {
      const checkValue = value;
      try {
        const result = await reach(validationSchema, control).validate(
          checkValue,
          {
            abortEarly: false,
          },
        );
        setValidationErrors((prev) => {
          const { [control]: validControl, ...rest } = prev;
          return rest;
        });
      } catch (error) {
        const { message } = error?.inner?.[0] || {};
        setValidationErrors((prev) => ({
          ...prev,
          [control]: message,
        }));
      }
    },
    [validationSchema],
  );

  /**
   * Sets value to the textfield
   * @param event Input event
   */
  const onChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setRequestData((current) => ({ ...current, [name]: value }));
      validateControl(name, value);
    },
    [validateControl],
  );

  /**
   * Sets value to the switch
   * @param event Input event
   */
  const onChangeSwitch = useCallback((event) => {
    const { name, checked } = event.target;
    setRequestData((current) => ({ ...current, [name]: !!checked }));
  }, []);

  /**
   * Shows form control field error message
   * @control field control name
   */
  const showValidationErrors = (control) => {
    if (validationErrors[control])
      return (
        <FormHelperText key={control} error>
          {validationErrors?.[control]}
        </FormHelperText>
      );
    return null;
  };

  const onToggleSwitch = useCallback(({ variable, value }) => {
    if (value === true)
      setRequestData((prev) => ({ ...prev, treatmentType: variable }));
  }, []);

  const resetForm = () => {
    setRequestData((prev) => ({
      ...initRequestFormData,
      organization: practitioner?.organization?.name,
      therapist: `${practitioner?.firstname} ${practitioner?.lastname}`,
    }));
  };

  /**
   * Shows form control field error message
   * @returns boolean value is form valid
   */
  const validateSubmitiion = async () => {
    let valid = true;

    const { dialogueEmotion, furtherNotes, ...validateFields } = requestData;
    Object.keys(validateFields).forEach((control) => {
      validateControl(control, requestData[control]);
    });

    try {
      await validationSchema.validate(requestData, { abortEarly: false });
      valid = true;
    } catch (error) {
      valid = false;
    }

    return valid;
  };

  /**
   * Update / create practitioner form submition
   * * Sets error if fields are not filled
   * @param event native submit event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    const valid = await validateSubmitiion();
    if (!valid) return;
    setRequestIsloading(true);

    const { interactiveSubject, ...relaxationData } = requestData;
    const videoRequestData = isExposure ? requestData : relaxationData;

    try {
      await videoRequestService.createVideoReuest(videoRequestData);
      showToast(t('forms.video_request_success'), 'success', 2000);
      setRequestIsloading(false);
      resetForm();
    } catch (error) {
      showToast(t('forms.video_request_error'), 'error', 2000);
    }
  };

  /**
   * generates formfield based on the control
   */
  const getFormControl = () =>
    Object.keys(requestData).map((control) => {
      const isMultiLine = multiLineInputs.includes(control);
      const isReadOnly = readOnlyInputs.includes(control);
      const isHidden = hiddenInputs.includes(control);
      const inputTitle = headerInputs.filter((item) => item.key === control)[0]
        ?.title;

      switch (control) {
        case 'treatmentType': {
          const toggleValue = requestData?.[control] === 'exposure';
          return (
            <Fragment key={control}>
              <Typography className='section-title' variant='h5'>
                {t('forms.treatment.target')}
              </Typography>
              <CombineToggle
                key={control}
                name='exposure'
                className='treatment-type'
                selectValue={toggleValue}
                options={switchOptions}
                onChangeHandler={onToggleSwitch}
              />
            </Fragment>
          );
        }

        case 'interactiveSubject': {
          const switchValue = !!requestData?.[control];
          return (
            <Fragment key={control}>
              <SwitchUi
                isShown={isExposure}
                unmountOnExit={!isDesktop}
                className='input-switch interactive-subject'
                checked={switchValue}
                label={t('Interactive subject')}
                labelPlacement='start'
                name='interactiveSubject'
                onChangeHandler={onChangeSwitch}
              />
            </Fragment>
          );
        }

        default: {
          if (isHidden) return;
          return (
            <Fragment key={control}>
              {inputTitle && (
                <Typography className='section-title' variant='h5'>
                  {t(`${inputTitle}`)}
                </Typography>
              )}
              {/*  eslint-disable-next-line react/jsx-max-props-per-line */}
              <Grid item xs={6} sm={6} className='form-input' key={control}>
                <TextFieldUi
                  className='simple-text-field'
                  label={t(`forms.${control}`)}
                  inputProps={{ readOnly: isReadOnly }}
                  autoFocus
                  name={control}
                  fullWidth
                  rows={isMultiLine ? 4 : null}
                  multiline={isMultiLine}
                  value={requestData?.[control] || ''}
                  handleChange={onChange}
                  disabled={isReadOnly}
                />
                {showValidationErrors(control)}
              </Grid>
            </Fragment>
          );
        }
      }
    });

  /**
   * Sets practitioner data if it exists
   */
  useEffect(() => {
    if (practitioner)
      setRequestData((prev) => ({
        ...prev,
        organization: practitioner?.organization?.name,
        therapist: `${practitioner?.firstname} ${practitioner?.lastname}`,
        email: email || '',
      }));
  }, []);

  return (
    <Box sx={requestVideo}>
      <TableHeader
        isButton={false}
        title='video-request.title'
        subTitle='video-request.subtitle'
      />
      <Box component='form' className='reqest-form' onSubmit={handleSubmit}>
        <Typography variant='h3' className='form-title'>
          {t('video-request.form_title')}
        </Typography>
        {getFormControl()}
        <ButtonUi
          className={classNames('action-btn', { loading: requestIsloading })}
          variant='contained'
          type='primary'
          size='small'
          btnType='submit'
          startIcon={requestIsloading ? <CircularProgress /> : null}
          disabled={isSubmitionDisabled}
        >
          {!requestIsloading && t('buttons.request')}
        </ButtonUi>
      </Box>
    </Box>
  );
});
